import {
  computed,
  DestroyRef,
  inject,
  Injectable,
  signal,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Route, RouteConfigLoadEnd, Router } from "@angular/router";
import { filter } from "rxjs/operators";
import { MenuMeta, MenuNode } from "../models/menu-meta";
import { FeatureFlagService } from "./feature-flag.service";
import { PermissionService } from "./permission.service";

/**
 * Builds the sidebar tree from the live `Router.config`.
 *
 * Each route that wants to appear in the sidebar declares its
 * label/icon/permission via `data: { menu: MenuMeta }` (see
 * `core/models/menu-meta.ts`). This service walks the router tree,
 * filters by the user's permission set, prunes empty groups, and
 * returns a MenuNode[] reactively. Sidebar order follows the order
 * routes appear in their parent array — no explicit `order` field.
 *
 * Why a service (instead of a static menu config):
 *   • single source of truth — the route declares both its guard
 *     and its sidebar metadata in one place
 *   • adding a new feature = annotate one route; no parallel file
 *   • sidebar is "wait until ready" by construction: returns []
 *     while permissions haven't loaded yet, so the layout can show
 *     a skeleton instead of a flash of empty/wrong items
 *
 * Lazy-loaded children: `provideRouter(routes, withPreloading(
 * PreloadAllModules))` is wired in `app.config.ts` so the lazy
 * chunks load in the background after bootstrap. We bump
 * `routeConfigVersion` on every `RouteConfigLoadEnd` event,
 * causing the `menu` computed to re-walk and pick up newly
 * loaded children. Until they load, only top-level entries are
 * visible — the user typically navigates and the rest streams in.
 */
@Injectable({ providedIn: "root" })
export class RouterMenuService {
  private readonly router = inject(Router);
  private readonly permissions = inject(PermissionService);
  private readonly featureFlags = inject(FeatureFlagService);
  private readonly destroyRef = inject(DestroyRef);

  /** Bumped after each lazy-load → forces `menu` to re-evaluate. */
  private readonly routeConfigVersion = signal(0);

  constructor() {
    this.router.events
      .pipe(
        filter((e) => e instanceof RouteConfigLoadEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.routeConfigVersion.update((v) => v + 1));
  }

  /**
   * Filtered sidebar tree (in route-array order). Empty array until
   * permissions have loaded — sidebars should treat that as "still
   * resolving" and render a skeleton instead of an empty state.
   */
  readonly menu = computed<MenuNode[]>(() => {
    if (!this.permissions.loaded()) return [];
    // Subscribe to the version signal so a lazy-route load triggers
    // a re-walk; we don't actually need the value.
    void this.routeConfigVersion();
    const owned = this.permissions.permissions();
    // Read the signal so an override() re-walks the tree.
    void this.featureFlags.flags();
    return walk(this.router.config, "", owned, this.featureFlags);
  });

  /** True once the first permission set has been delivered. */
  readonly ready = computed(() => this.permissions.loaded());
}

function walk(
  routes: readonly Route[],
  parentPath: string,
  perms: ReadonlySet<string>,
  flags: FeatureFlagService,
): MenuNode[] {
  const out: MenuNode[] = [];
  for (const route of routes) {
    const meta = readMenuMeta(route);
    const fullPath = joinPath(parentPath, route.path ?? "");

    const childRoutes = route.children ?? loadedChildren(route) ?? [];
    const children = walk(childRoutes, fullPath, perms, flags);

    if (!meta) {
      // Route has no menu metadata; surface its menu-bearing
      // descendants directly (so an unannotated group container
      // doesn't hide everything beneath it).
      out.push(...children);
      continue;
    }

    const allowed =
      checkFeatureFlag(meta, flags) && checkPermission(meta, perms);

    if (meta.group) {
      // Group header — keep only when the user holds the group's own
      // permission (if it declares one) AND at least one child survives.
      // A permission-less container (checkPermission → true) still shows
      // purely on surviving children, preserving the "parent unguarded,
      // children carry their own read perms" pattern. Without the
      // `allowed` guard a group that declares a requiredPermission is
      // shown whenever any child survives — and children with no perm of
      // their own always survive — so the declared perm never gated it.
      if (!allowed || children.length === 0) continue;
      out.push(toNode(meta, fullPath, children));
      continue;
    }

    if (!allowed) continue;
    out.push(toNode(meta, fullPath, children));
  }
  return out;
}

function readMenuMeta(route: Route): MenuMeta | undefined {
  const data = route.data as { menu?: MenuMeta } | undefined;
  return data?.menu;
}

/**
 * Reads children that Angular has populated on a lazy-loaded
 * route after `RouteConfigLoadEnd`. Angular stashes them on the
 * `_loadedRoutes` private field on `Route` — we read defensively
 * so that breaks in future Angular versions degrade gracefully
 * (we just won't show those children until preload completes).
 */
function loadedChildren(route: Route): Route[] | undefined {
  const loaded = (route as { _loadedRoutes?: Route[] })._loadedRoutes;
  return loaded;
}

function joinPath(parent: string, segment: string): string {
  if (!segment) return parent;
  if (segment.startsWith("/")) return segment;
  if (!parent) return `/${segment}`;
  return `${parent}/${segment}`;
}

function toNode(meta: MenuMeta, path: string, children: MenuNode[]): MenuNode {
  const node: MenuNode = {
    name: meta.label,
    path,
    icon: meta.icon,
    group: meta.group ?? false,
    children,
  };

  if (meta.dashboardLabel) {
    node.dashboardLabel = meta.dashboardLabel;
  }

  return node;
}

/**
 * A flagged-off entry is hidden outright — including a group header, which is
 * pruned before its children are even considered, so turning a flag off removes
 * the whole branch rather than leaving an empty heading.
 */
function checkFeatureFlag(meta: MenuMeta, flags: FeatureFlagService): boolean {
  if (!meta.requiredFeatureFlag) return true;
  const list = Array.isArray(meta.requiredFeatureFlag)
    ? meta.requiredFeatureFlag
    : [meta.requiredFeatureFlag];
  return flags.getAll(list);
}

function checkPermission(meta: MenuMeta, owned: ReadonlySet<string>): boolean {
  // `requiredAnyPermission` short-circuits — it's the multi-tab
  // case where any single perm is enough to reach a usable subset
  // of the page. Treat as mutually exclusive with the all-of form
  // (declare one or the other, never both) per MenuMeta contract.
  if (meta.requiredAnyPermission && meta.requiredAnyPermission.length > 0) {
    return meta.requiredAnyPermission.some((p) => owned.has(p));
  }
  if (!meta.requiredPermission) return true;
  const list = Array.isArray(meta.requiredPermission)
    ? meta.requiredPermission
    : [meta.requiredPermission];
  if (list.length === 0) return true;
  return list.every((p) => owned.has(p));
}
